import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DataGrid from '../../../../common/DataGrid/DataGrid.jsx';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';

const StudyProgramsTable = ({ studyPrograms, onDetailClick, onDelete }) => {
  const { t } = useTranslation();

  const nameTextFilterProps = useDataGridTextFilter('name');
  const shortcutTextFilterProps = useDataGridTextFilter('shortcut');
  const codeInStGroupTextFilterProps = useDataGridTextFilter('code_in_st_group');
  const wsCodeTextFilterProps = useDataGridTextFilter('ws_code');
  const guarantorTextFilterProps = useDataGridTextFilter('guarantor.full_name');
  const studyProgramTypeFilterProps = useMemo(() => {
    const types = (studyPrograms || []).reduce((options, program) => {
      const typeId = program.study_program_type?.id;
      const typeName = program.study_program_type?.name;

      if (typeId && typeName && !options[typeId]) {
        options[typeId] = typeName;
      }

      return options;
    }, {});

    return Object.fromEntries(
      Object.entries(types).sort(([, left], [, right]) => left.localeCompare(right, 'sk')),
    );
  }, [studyPrograms]);
  const studyProgramTypeFilters = useMemo(
    () => Object.entries(studyProgramTypeFilterProps).map(([value, label]) => ({
      text: label,
      value: Number(value),
    })),
    [studyProgramTypeFilterProps],
  );

  const columns = useMemo(
    () => [
      {
        title: t('Názov'),
        dataIndex: 'name',
        ellipsis: true,
        ...nameTextFilterProps,
        sorter: dataGridTextSorter('name'),
        width: "20%"
      },
      {
        title: t('Stupeň'),
        dataIndex: ['study_program_type', 'name'],
        width: 200,
        sorter: (a, b) => {
          const aType = a.study_program_type?.name || '';
          const bType = b.study_program_type?.name || '';
          return aType.localeCompare(bType);
        },
        render: (_, record) => record.study_program_type?.name || '-',
        filters: studyProgramTypeFilters,
        onFilter: (value, record) =>
          Number(record.study_program_type_id ?? 0) === Number(value),
      },
      {
        title: t('Garant'),
        dataIndex: ['guarantor', 'full_name'],
        sorter: (a, b) => {
          const aGuarantor = a.guarantor?.full_name || '';
          const bGuarantor = b.guarantor?.full_name || '';
          return aGuarantor.localeCompare(bGuarantor);
        },
        render: (_, record) => record.guarantor?.full_name || '-',
        ...guarantorTextFilterProps,
        width: 300,
      },
      {
        title: t('Skratka'),
        dataIndex: 'shortcut',
        width: 120,
        ...shortcutTextFilterProps,
        sorter: dataGridTextSorter('shortcut'),
      },
      {
        title: t('Znak v št.skupine'),
        dataIndex: 'code_in_st_group',
        width: 170,
        sorter: dataGridTextSorter('code_in_st_group'),
        render: (value) => value || '-',
        ...codeInStGroupTextFilterProps,
      },
      {
        title: t('WS kód'),
        dataIndex: 'ws_code',
        width: 120,
        sorter: dataGridTextSorter('ws_code'),
        render: (ws_code) => ws_code || '-',
        ...wsCodeTextFilterProps,
      },
    ],
    [
      nameTextFilterProps,
      shortcutTextFilterProps,
      codeInStGroupTextFilterProps,
      studyProgramTypeFilters,
      wsCodeTextFilterProps,
      guarantorTextFilterProps,
      t,
    ],
  );

  return (
    <DataGrid
      rowKey={(program) => program.id}
      columns={columns}
      dataSource={studyPrograms || []}
      permissionsBasic={'study-programs'}
      uniqueTableId={'study-programs-table'}
      onRowClick={onDetailClick}
      onEditClick={onDetailClick}
      optionsWidth={112}
      onDelete={onDelete}
    />
  );
};

StudyProgramsTable.propTypes = {
  studyPrograms: PropTypes.array,
  onDetailClick: PropTypes.func,
  onDelete: PropTypes.func,
  onCreateClick: PropTypes.func,
};

export default StudyProgramsTable;
